// src/services/alertWebSocket.js
import { eventEmitter } from '../utils/eventEmitter';
import { webSocketUrl } from '../constants/config';

class AlertWebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimeout = null;
    this.organizationId = null;
    this.userId = null;
    this.isSubscribed = false;
    this.connectionListeners = [];
  }

  /**
   * Connect to WebSocket and subscribe to alerts
   */
  connect(organizationId, userId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.organizationId = organizationId;
    this.userId = userId;

    // ✅ Use the config URL
    const wsUrl = webSocketUrl;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Subscribe to alerts immediately
        this.subscribeToAlerts();
        
        // Notify listeners
        this.notifyConnectionListeners(true);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log('📨 WebSocket message received:', data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error(' WebSocket error:', error);
      };

      this.ws.onclose = () => {
        // console.log('🔌 WebSocket disconnected');
        this.isConnected = false;
        this.isSubscribed = false;
        this.notifyConnectionListeners(false);
        this.autoReconnect();
      };

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.autoReconnect();
    }
  }

  /**
   * Subscribe to alerts for the organization
   */
  subscribeToAlerts() {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // console.log('Cannot subscribe: WebSocket not connected');
      return;
    }

    if (this.isSubscribed) {
      // console.log('Already subscribed to alerts');
      return;
    }

    const subscriptionMessage = {
      type: 'subscribe_alerts',
      organizationId: this.organizationId || 'default',
      userId: this.userId || 'anonymous',
      alertTypes: ['intrusion', 'loitering']
    };

    this.ws.send(JSON.stringify(subscriptionMessage));
    this.isSubscribed = true;
    // console.log('📡 Subscribed to alerts:', subscriptionMessage);
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    // Handle subscription confirmation
    if (data.type === 'subscribed') {
      // console.log('✅ Alert subscription confirmed:', data.message);
      return;
    }

    // Handle unsubscription confirmation
    if (data.type === 'unsubscribed') {
      // console.log('✅ Alert unsubscription confirmed:', data.message);
      this.isSubscribed = false;
      return;
    }

    // Handle new alert
    if (data.type === 'new_alert') {
      // console.log('🔔 New alert received via WebSocket:', data.data);
      
      // Only emit for intrusion and loitering
      if (data.data.type === 'intrusion' || data.data.type === 'loitering') {
        // console.log('✅ Emitting newAlert event for type:', data.data.type);
        eventEmitter.emit('newAlert', data.data);
      } else {
        // console.log('⏭️ Skipping alert type:', data.data.type);
      }
      return;
    }

    // Handle errors
    if (data.type === 'error') {
      console.error('WebSocket error message:', data.message);
      return;
    }

    // Handle any other messages
    // console.log('📨 Other message type:', data.type);
  }

  /**
   * Auto-reconnect with exponential backoff
   */
  autoReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // console.log('❌ Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(3000 * Math.pow(1.5, this.reconnectAttempts), 30000);

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    this.reconnectTimeout = setTimeout(() => {
      if (this.organizationId) {
        this.connect(this.organizationId, this.userId);
      }
    }, delay);
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({
            type: 'unsubscribe_alerts'
          }));
        } catch (e) {
          // Ignore
        }
      }
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.isSubscribed = false;
    this.notifyConnectionListeners(false);
  }

  /**
   * Check connection status
   */
  isConnectedToStream() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isSubscribed: this.isSubscribed,
      readyState: this.ws ? this.ws.readyState : null,
      organizationId: this.organizationId,
      userId: this.userId
    };
  }

  /**
   * Add connection status listener
   */
  onConnectionChange(listener) {
    this.connectionListeners.push(listener);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify connection listeners
   */
  notifyConnectionListeners(isConnected) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(isConnected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }
}

export default new AlertWebSocketService();